/**
 * Users and role grants — `docs/API.md` §3.
 * Mirrors `salescrmapi/src/modules/users`.
 */
import apiClient, { ifMatch, idempotencyKey } from './axiosClient.js';

/**
 * A page of users.
 *
 * **The shape of each row depends on the caller.** With `admin.users:view` the
 * full record comes back — mobile, joining date, role grants. Without it, the
 * same URL returns a picker projection (id, name, email, team, status), because
 * a rep must be able to reassign a lead without reading HR data. Screens that
 * need the full record must check the permission first rather than assuming.
 *
 * Pagination is cursor-based (§1.4): pass `page.next_cursor` from the previous
 * response as `cursor`. There is no page number and no offset.
 *
 * @param {{limit?:number, cursor?:string, status?:string, role?:string,
 *          team_id?:string, territory_id?:string, manager_id?:string,
 *          is_licensed?:boolean, q?:string, include_deleted?:boolean}} params
 * @returns {Promise<{data:object[], page:object}>}
 */
export async function listUsers(params = {}) {
  const { data } = await apiClient.get('/users', { params });
  return data;
}

/**
 * One user. Accepts the literal `me` as well as an id.
 *
 * A user outside the caller's visibility answers `404`, not `403` — confirming
 * that a record exists is itself information the caller is not entitled to.
 *
 * @param {string} id a user id, or 'me'
 */
export async function getUser(id) {
  const { data } = await apiClient.get(`/users/${id}`);
  return data;
}

/**
 * Create a user and send an invitation.
 *
 * The user lands in `invited` status with no credential; they set a password or
 * sign in through SSO on first use. Roles are supplied inline because a user
 * with no role can sign in and see nothing, which reads as a broken system
 * rather than a pending setup step.
 *
 * Carries an `Idempotency-Key`, so a retried Save cannot send two invitations.
 *
 * @param {object} payload see CreateUserRequest in the OpenAPI document
 */
export async function createUser(payload) {
  const { data } = await apiClient.post('/users', payload, { headers: idempotencyKey() });
  return data;
}

/**
 * Update a profile.
 *
 * `rowVersion` is required (§1.7): it is the version the user was looking at
 * when they started editing. If someone else has saved in the meantime the call
 * throws `stale_write`, and `error.payload` carries `current_version`,
 * `changed_fields` and the full `current` record so the UI can offer a
 * three-way merge instead of discarding the edit.
 *
 * Roles are **not** editable here — see `grantRole`. A permission change gets
 * its own audited call with a reason rather than riding along on a profile save.
 *
 * @param {string} id
 * @param {object} patch only the fields being changed
 * @param {number} rowVersion from the record the user was editing
 */
export async function updateUser(id, patch, rowVersion) {
  const { data } = await apiClient.patch(`/users/${id}`, patch, { headers: ifMatch(rowVersion) });
  return data;
}

/**
 * Activate, suspend, disable, lock or unlock a user.
 *
 * Anything other than `active` revokes every session that user holds
 * immediately — the response reports how many — rather than waiting for tokens
 * to expire. A reason is required for anything that restricts access.
 *
 * @param {string} id
 * @param {{status:'active'|'suspended'|'disabled'|'locked'|'invited', reason?:string}} payload
 * @returns {Promise<{status:string, sessions_revoked:number}>}
 */
export async function setUserStatus(id, payload) {
  const { data } = await apiClient.post(`/users/${id}/status`, payload);
  return data;
}

/**
 * A user's active role grants, each with its scope, validity window and source.
 * A grant whose `source` is `sso_claim` came from the directory and will be
 * re-created on next sign-in if revoked by hand.
 *
 * @param {string} id a user id, or 'me'
 */
export async function listUserRoles(id) {
  const { data } = await apiClient.get(`/users/${id}/roles`);
  return data.data;
}

/**
 * Grant a role, optionally scoped to a team or territory and optionally time-boxed.
 *
 * The response carries `effect_preview` — the screens, operations and data-scope
 * widenings this grant produces. Show it: "what can this person now see that
 * they could not before" is the question an administrator actually has, and a
 * bare confirmation does not answer it.
 *
 * Throws `privilege_escalation` if the role carries permissions the caller does
 * not hold, `duplicate_grant` if the same role is already active at that scope,
 * or `scope_required` if the role's `default_scope` demands a team or territory.
 *
 * @param {string} id
 * @param {object} payload see GrantRoleRequest
 */
export async function grantRole(id, payload) {
  const { data } = await apiClient.post(`/users/${id}/roles`, payload, {
    headers: idempotencyKey(),
  });
  return data;
}

/**
 * Revoke a grant.
 *
 * Sets `revoked_at` rather than deleting the row — an expired permission is
 * evidence, and "who could approve this last March" gets asked.
 *
 * @param {string} id user id
 * @param {string} grantId the grant's own id, not the role id
 */
export async function revokeRole(id, grantId) {
  await apiClient.delete(`/users/${id}/roles/${grantId}`);
}

/**
 * Ask whether a user may do specific things — the "test as user" panel.
 *
 * Each result carries `granted_by` and `denied_by` naming the roles that decided
 * it. That is the whole value of the endpoint: without it an administrator is
 * left guessing which of a user's four roles is blocking them.
 *
 * @param {string} id a user id, or 'me'
 * @param {{screen:string, operation:string}[]} checks
 * @returns {Promise<object[]>} one result per check, in the order asked
 */
export async function checkPermissions(id, checks) {
  const { data } = await apiClient.post(`/users/${id}/permissions/check`, { checks });
  return data.results;
}
